import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { RevealLine } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const HERO_SLIDES = [
  "/api/files/dfe-website/uploads/project-p19-0.jpg",
  "/api/files/dfe-website/uploads/project-p17-0.jpg",
  "/api/files/dfe-website/uploads/hero-stage.jpg",
  "/api/files/dfe-website/uploads/project-p10-0.jpg",
];

export default function Hero() {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 overflow-hidden" data-testid="hero-slideshow">
        <AnimatePresence>
          <motion.img
            key={slide}
            src={HERO_SLIDES[slide]}
            alt="Concert stage"
            initial={{ opacity: 0, scale: 1.14 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ opacity: { duration: 1.6, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
            className="absolute inset-0 w-full h-[120%] object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/50" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative max-w-[1800px] w-full mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono2 text-[10px] md:text-xs text-[#ffde00] tracking-[0.4em] mb-6"
        >
          DREAM FACTORY ENTERTAINMENT — EST. 2009 · MALAYSIA
        </motion.p>

        <h1 className="font-display uppercase leading-[0.88] text-[#f5f5f5] text-[17vw] md:text-[11vw]">
          <RevealLine delay={0.25}>Where Music</RevealLine>
          <RevealLine delay={0.4} className="text-outline">
            Meets
          </RevealLine>
          <RevealLine delay={0.55}>
            Dreams<span className="text-[#ffde00]">.</span>
          </RevealLine>
        </h1>

        <div className="mt-8 md:mt-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl"
          >
            <p className="font-zh text-base md:text-lg text-[#a1a1aa] leading-relaxed">
              {lang === "zh" ? (
                <>
                  娛樂夢工廠 — 演唱会制作 × 艺人管理 × 音乐节策划。
                  <span className="text-[#f5f5f5]">自 2009 年起，把世界级舞台带回马来西亚与亚洲。</span>
                </>
              ) : (
                <>
                  Dream Factory Entertainment — concert production × artist management × festivals.{" "}
                  <span className="text-[#f5f5f5]">Bringing world-class stages back to Malaysia and Asia since 2009.</span>
                </>
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-cta-contact" className="btn-yellow">
                {lang === "zh" ? "商业合作" : "BUSINESS ENQUIRY"}
                <ArrowUpRight size={16} />
              </Link>
              <Link to="/works" data-testid="hero-cta-works" className="btn-ghost">
                {lang === "zh" ? "探索作品" : "EXPLORE WORKS"}
              </Link>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="hidden md:flex flex-col items-end gap-1 font-mono2 text-[10px] text-[#52525b] tracking-[0.3em]"
          >
            <span>SCROLL</span>
            <ArrowDown size={16} className="text-[#ffde00] animate-bounce" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
